import React, { useState, useEffect } from 'react';
import {
  X,
  HelpCircle,
  Keyboard,
  BookOpen,
  MessageCircle,
  Phone,
  Mail,
  Globe,
  Info,
  ChevronDown,
  FileText,
  Download,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useKeycloak } from '../../../contexts/KeycloakContext';
import { listSupportManuals, formatFileSize, SupportManual } from '../../../services/supportManualService';

interface HelpCenterModalProps {
  open: boolean;
  onClose: () => void;
}

// Keyboard shortcuts data
const KEYBOARD_SHORTCUTS = {
  navigation: [
    { keys: ['Ctrl', '1-9'], description: 'Alternar entre abas de clientes (1ª aba = Ctrl+1)' },
    { keys: ['Escape'], description: 'Fechar modais, menus e limpar campos de busca' },
  ],
  search: [
    { keys: ['Tab'], description: 'Navegar entre campos de busca rápida' },
    { keys: ['↑', '↓'], description: 'Navegar pelos resultados da busca' },
    { keys: ['Enter'], description: 'Selecionar resultado destacado' },
  ],
  forms: [
    { keys: ['Ctrl', 'Enter'], description: 'Enviar formulários e avisos' },
  ],
};

// Feature guides data
const FEATURE_GUIDES = {
  visitors: [
    {
      title: 'Autorizar Visitante',
      description: 'Cadastre e autorize visitantes para acesso ao condomínio',
      steps: [
        'Clique em "Cadastros" ou use a busca rápida de visitantes',
        'Selecione "Cadastrar novo visitante"',
        'Preencha os dados pessoais do visitante',
        'Defina o período de autorização (data/hora início e fim)',
        'Capture a foto (opcional mas recomendado)',
        'Salve o cadastro',
      ],
    },
    {
      title: 'Entrada e Saída Manual',
      description: 'Registre entradas e saídas manualmente quando necessário',
      steps: [
        'Localize o visitante na lista de visitantes ativos',
        'Clique no menu de ações (três pontos)',
        'Selecione "Entrada Manual" ou "Saída"',
        'Confirme a ação',
      ],
    },
  ],
  residents: [
    {
      title: 'Cadastrar Morador',
      description: 'Adicione novos moradores ao sistema',
      steps: [
        'Use a busca de moradores e clique em "Cadastrar novo morador"',
        'Preencha dados pessoais (nome, CPF, telefone, email)',
        'Vincule a um usuário do sistema (opcional)',
        'Selecione a unidade e tipo de relação (proprietário, inquilino, etc.)',
        'Capture foto de identificação',
        'Salve o cadastro',
      ],
    },
  ],
  devices: [
    {
      title: 'Acionar Dispositivos',
      description: 'Controle remotamente catracas, portões e fechaduras',
      steps: [
        'Localize o dispositivo na botoeira (parte inferior da tela)',
        'Clique no dispositivo desejado',
        'Selecione "Acionar" para abrir porta/portão',
        'Aguarde confirmação visual (verde = sucesso, vermelho = erro)',
      ],
    },
  ],
  notes: [
    {
      title: 'Criar Aviso',
      description: 'Crie avisos globais ou específicos por unidade/departamento',
      steps: [
        'Clique no botão "+" no banner de avisos (amarelo no topo)',
        'Digite o conteúdo do aviso',
        'O aviso será criado para o contexto atual (global ou unidade/departamento)',
        'Pressione Ctrl+Enter ou clique em "Salvar Aviso"',
      ],
    },
  ],
};

// FAQ data
const FAQ_ITEMS = [
  {
    question: 'Como funciona a autorização de visitantes?',
    answer:
      'Visitantes podem ser autorizados através do cadastro manual com definição de período de acesso. Durante o período autorizado, eles podem entrar usando reconhecimento facial ou RFID. O sistema registra automaticamente as entradas e saídas.',
  },
  {
    question: 'Qual a diferença entre abas de clientes?',
    answer:
      'Cada aba representa um cliente (condomínio ou empresa) diferente. Use Ctrl+1, Ctrl+2, etc. para alternar rapidamente entre clientes. Você pode ter até 9 abas abertas simultaneamente.',
  },
  {
    question: 'Como visualizar o histórico de eventos?',
    answer:
      'O painel de eventos do lado direito mostra os eventos em tempo real. Para ver o histórico completo de um dispositivo, clique no dispositivo na botoeira e selecione "Histórico".',
  },
  {
    question: 'O que significa o indicador de conexão?',
    answer:
      'O indicador no canto superior direito mostra o status da conexão com o servidor. Verde = conectado, Vermelho = desconectado. Clique nele para ver mais detalhes.',
  },
  {
    question: 'Como alternar entre modo operacional e modo normal?',
    answer:
      'Clique no seu nome de usuário no canto superior esquerdo e selecione "Voltar ao modo normal". No modo normal, você tem acesso completo ao sistema. No modo operacional, a interface é otimizada para operações rápidas.',
  },
];

// Custom scrollbar styles
const scrollbarStyles = `
  .help-modal-scrollbar::-webkit-scrollbar {
    width: 8px;
  }

  .help-modal-scrollbar::-webkit-scrollbar-track {
    background: #111827;
    border-radius: 4px;
  }

  .help-modal-scrollbar::-webkit-scrollbar-thumb {
    background: #374151;
    border-radius: 4px;
  }

  .help-modal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #4b5563;
  }

  /* Firefox */
  .help-modal-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #374151 #111827;
  }
`;

// KeyBadge component
interface KeyBadgeProps {
  keys: string[];
}

const KeyBadge: React.FC<KeyBadgeProps> = ({ keys }) => {
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      {keys.map((key, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span className="text-gray-500 text-xs">+</span>}
          <kbd className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs font-mono text-gray-200 shadow-sm">
            {key}
          </kbd>
        </React.Fragment>
      ))}
    </div>
  );
};

// CollapsibleSection component
interface CollapsibleSectionProps {
  id: string;
  title: string;
  icon: React.ElementType;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  id,
  title,
  icon: Icon,
  isOpen,
  onToggle,
  children,
}) => {
  return (
    <div className="border-b border-gray-700 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-800/50 transition-colors"
        aria-expanded={isOpen}
        aria-controls={`section-${id}`}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Icon className="h-5 w-5 text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <ChevronDown
          className={cn(
            'h-5 w-5 text-gray-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div id={`section-${id}`} className="px-6 pb-6 pt-2">
          {children}
        </div>
      )}
    </div>
  );
};

// Main component
export const HelpCenterModal: React.FC<HelpCenterModalProps> = ({ open, onClose }) => {
  // State for collapsible sections (keyboard shortcuts open by default)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['keyboard']));
  const [manuals, setManuals] = useState<SupportManual[]>([]);
  const [loadingManuals, setLoadingManuals] = useState(false);
  const { token } = useKeycloak();

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Fetch manuals when modal opens
  useEffect(() => {
    if (!open || !token) return;
    setLoadingManuals(true);
    listSupportManuals(token)
      .then(setManuals)
      .catch(() => {})
      .finally(() => setLoadingManuals(false));
  }, [open, token]);

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

        {/* Modal */}
        <div className="relative w-full max-w-3xl max-h-[85vh] bg-gray-900 rounded-xl shadow-2xl border border-gray-700 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <HelpCircle className="h-6 w-6 text-blue-400" />
              <h2 className="text-xl font-bold text-white">Central de Ajuda</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-800 rounded-lg transition-colors"
              aria-label="Fechar"
            >
              <X className="h-5 w-5 text-gray-400 hover:text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto help-modal-scrollbar">
            {/* Section 1: Keyboard Shortcuts */}
            <CollapsibleSection
              id="keyboard"
              title="Atalhos de Teclado"
              icon={Keyboard}
              isOpen={openSections.has('keyboard')}
              onToggle={() => toggleSection('keyboard')}
            >
              <div className="space-y-6">
                {/* Navigation */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Navegação
                  </h4>
                  <div className="space-y-2">
                    {KEYBOARD_SHORTCUTS.navigation.map((shortcut, i) => (
                      <div key={i} className="flex items-center justify-between gap-4 py-2">
                        <span className="text-sm text-gray-300">{shortcut.description}</span>
                        <KeyBadge keys={shortcut.keys} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Search */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Busca
                  </h4>
                  <div className="space-y-2">
                    {KEYBOARD_SHORTCUTS.search.map((shortcut, i) => (
                      <div key={i} className="flex items-center justify-between gap-4 py-2">
                        <span className="text-sm text-gray-300">{shortcut.description}</span>
                        <KeyBadge keys={shortcut.keys} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Forms */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Formulários
                  </h4>
                  <div className="space-y-2">
                    {KEYBOARD_SHORTCUTS.forms.map((shortcut, i) => (
                      <div key={i} className="flex items-center justify-between gap-4 py-2">
                        <span className="text-sm text-gray-300">{shortcut.description}</span>
                        <KeyBadge keys={shortcut.keys} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 2: Feature Guides */}
            <CollapsibleSection
              id="guides"
              title="Guias de Funcionalidades"
              icon={BookOpen}
              isOpen={openSections.has('guides')}
              onToggle={() => toggleSection('guides')}
            >
              <div className="space-y-6">
                {/* Visitors */}
                <div>
                  <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-3">
                    Visitantes
                  </h4>
                  <div className="space-y-4">
                    {FEATURE_GUIDES.visitors.map((guide, i) => (
                      <div
                        key={i}
                        className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50"
                      >
                        <h5 className="text-sm font-semibold text-white mb-2">{guide.title}</h5>
                        <p className="text-sm text-gray-400 mb-3">{guide.description}</p>
                        <ol className="space-y-1.5 ml-5">
                          {guide.steps.map((step, j) => (
                            <li key={j} className="text-sm text-gray-300 list-decimal">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Residents */}
                <div>
                  <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-3">
                    Moradores
                  </h4>
                  <div className="space-y-4">
                    {FEATURE_GUIDES.residents.map((guide, i) => (
                      <div
                        key={i}
                        className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50"
                      >
                        <h5 className="text-sm font-semibold text-white mb-2">{guide.title}</h5>
                        <p className="text-sm text-gray-400 mb-3">{guide.description}</p>
                        <ol className="space-y-1.5 ml-5">
                          {guide.steps.map((step, j) => (
                            <li key={j} className="text-sm text-gray-300 list-decimal">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Devices */}
                <div>
                  <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-3">
                    Dispositivos
                  </h4>
                  <div className="space-y-4">
                    {FEATURE_GUIDES.devices.map((guide, i) => (
                      <div
                        key={i}
                        className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50"
                      >
                        <h5 className="text-sm font-semibold text-white mb-2">{guide.title}</h5>
                        <p className="text-sm text-gray-400 mb-3">{guide.description}</p>
                        <ol className="space-y-1.5 ml-5">
                          {guide.steps.map((step, j) => (
                            <li key={j} className="text-sm text-gray-300 list-decimal">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-3">
                    Avisos
                  </h4>
                  <div className="space-y-4">
                    {FEATURE_GUIDES.notes.map((guide, i) => (
                      <div
                        key={i}
                        className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50"
                      >
                        <h5 className="text-sm font-semibold text-white mb-2">{guide.title}</h5>
                        <p className="text-sm text-gray-400 mb-3">{guide.description}</p>
                        <ol className="space-y-1.5 ml-5">
                          {guide.steps.map((step, j) => (
                            <li key={j} className="text-sm text-gray-300 list-decimal">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 3: Manuais */}
            {(loadingManuals || manuals.length > 0) && (
              <CollapsibleSection
                id="manuals"
                title="Manuais e Documentos"
                icon={FileText}
                isOpen={openSections.has('manuals')}
                onToggle={() => toggleSection('manuals')}
              >
                {loadingManuals ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {manuals.map(manual => (
                      <div key={manual.id} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50">
                        <div className="flex-shrink-0 p-2 bg-red-500/20 rounded-lg">
                          <FileText className="h-4 w-4 text-red-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{manual.title}</p>
                          {manual.description && (
                            <p className="text-xs text-gray-400 mt-0.5 truncate">{manual.description}</p>
                          )}
                          <p className="text-xs text-gray-600 mt-0.5">{formatFileSize(manual.file_size)}</p>
                        </div>
                        <a
                          href={manual.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </CollapsibleSection>
            )}

            {/* Section 4: FAQ */}
            <CollapsibleSection
              id="faq"
              title="Perguntas Frequentes"
              icon={MessageCircle}
              isOpen={openSections.has('faq')}
              onToggle={() => toggleSection('faq')}
            >
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, i) => (
                  <div
                    key={i}
                    className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50"
                  >
                    <h5 className="text-sm font-semibold text-white mb-2">{faq.question}</h5>
                    <p className="text-sm text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Section 4: Support */}
            <CollapsibleSection
              id="support"
              title="Suporte e Contato"
              icon={Phone}
              isOpen={openSections.has('support')}
              onToggle={() => toggleSection('support')}
            >
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                  <Mail className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white mb-0.5">Email de Suporte</p>
                    <a
                      href="mailto:suporte@panther.com.br"
                      className="text-sm text-blue-400 hover:underline"
                    >
                      suporte@panther.com.br
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                  <Globe className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white mb-0.5">Documentação Online</p>
                    <a
                      href="https://docs.panther.com.br"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-400 hover:underline"
                    >
                      docs.panther.com.br
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                  <Info className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white mb-0.5">Versão do Sistema</p>
                    <p className="text-sm text-gray-400">
                      Panther Access {process.env.REACT_APP_VERSION || '0.1.0'}
                      {process.env.NODE_ENV === 'development' && process.env.REACT_APP_BUILD_COMMIT && (
                        <span className="ml-2 text-xs opacity-60">
                          ({process.env.REACT_APP_BUILD_COMMIT})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Panel version (renders inline in the main frame, no modal overlay) ─────────

interface HelpCenterPanelProps {
  onBack?: () => void;
}

export const HelpCenterPanel: React.FC<HelpCenterPanelProps> = ({ onBack }) => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['keyboard']));
  const [manuals, setManuals] = useState<SupportManual[]>([]);
  const [loadingManuals, setLoadingManuals] = useState(false);
  const { token } = useKeycloak();

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!token) return;
    setLoadingManuals(true);
    listSupportManuals(token)
      .then(setManuals)
      .catch(() => {})
      .finally(() => setLoadingManuals(false));
  }, [token]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-900">
      <style>{scrollbarStyles}</style>

      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-2 px-4 py-3 border-b border-gray-800 bg-gray-950">
        {onBack && (
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <HelpCircle className="h-4 w-4 text-blue-400 flex-shrink-0" />
        <span className="text-sm font-bold text-white">Central de Ajuda</span>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto help-modal-scrollbar">
        {/* Keyboard Shortcuts */}
        <CollapsibleSection
          id="keyboard"
          title="Atalhos de Teclado"
          icon={Keyboard}
          isOpen={openSections.has('keyboard')}
          onToggle={() => toggleSection('keyboard')}
        >
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Navegação</h4>
              <div className="space-y-2">
                {KEYBOARD_SHORTCUTS.navigation.map((shortcut, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-2">
                    <span className="text-sm text-gray-300">{shortcut.description}</span>
                    <KeyBadge keys={shortcut.keys} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Busca</h4>
              <div className="space-y-2">
                {KEYBOARD_SHORTCUTS.search.map((shortcut, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-2">
                    <span className="text-sm text-gray-300">{shortcut.description}</span>
                    <KeyBadge keys={shortcut.keys} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Formulários</h4>
              <div className="space-y-2">
                {KEYBOARD_SHORTCUTS.forms.map((shortcut, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-2">
                    <span className="text-sm text-gray-300">{shortcut.description}</span>
                    <KeyBadge keys={shortcut.keys} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Feature Guides */}
        <CollapsibleSection
          id="guides"
          title="Guias de Funcionalidades"
          icon={BookOpen}
          isOpen={openSections.has('guides')}
          onToggle={() => toggleSection('guides')}
        >
          <div className="space-y-6">
            {(['visitors', 'residents', 'devices', 'notes'] as const).map((key) => {
              const labels: Record<string, string> = { visitors: 'Visitantes', residents: 'Moradores', devices: 'Dispositivos', notes: 'Avisos' };
              return (
                <div key={key}>
                  <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-3">{labels[key]}</h4>
                  <div className="space-y-4">
                    {FEATURE_GUIDES[key].map((guide, i) => (
                      <div key={i} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                        <h5 className="text-sm font-semibold text-white mb-2">{guide.title}</h5>
                        <p className="text-sm text-gray-400 mb-3">{guide.description}</p>
                        <ol className="space-y-1.5 ml-5">
                          {guide.steps.map((step, j) => (
                            <li key={j} className="text-sm text-gray-300 list-decimal">{step}</li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleSection>

        {/* Manuais */}
        {(loadingManuals || manuals.length > 0) && (
          <CollapsibleSection
            id="manuals"
            title="Manuais e Documentos"
            icon={FileText}
            isOpen={openSections.has('manuals')}
            onToggle={() => toggleSection('manuals')}
          >
            {loadingManuals ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
              </div>
            ) : (
              <div className="space-y-2">
                {manuals.map(manual => (
                  <div key={manual.id} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50">
                    <div className="flex-shrink-0 p-2 bg-red-500/20 rounded-lg">
                      <FileText className="h-4 w-4 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{manual.title}</p>
                      {manual.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{manual.description}</p>}
                      <p className="text-xs text-gray-600 mt-0.5">{formatFileSize(manual.file_size)}</p>
                    </div>
                    <a href={manual.file_url} target="_blank" rel="noopener noreferrer"
                      className="flex-shrink-0 p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg transition-colors" title="Download">
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
        )}

        {/* FAQ */}
        <CollapsibleSection
          id="faq"
          title="Perguntas Frequentes"
          icon={MessageCircle}
          isOpen={openSections.has('faq')}
          onToggle={() => toggleSection('faq')}
        >
          <div className="space-y-4">
            {FAQ_ITEMS.map((faq, i) => (
              <div key={i} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <h5 className="text-sm font-semibold text-white mb-2">{faq.question}</h5>
                <p className="text-sm text-gray-400 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* Support */}
        <CollapsibleSection
          id="support"
          title="Suporte e Contato"
          icon={Phone}
          isOpen={openSections.has('support')}
          onToggle={() => toggleSection('support')}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
              <Mail className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-white mb-0.5">Email de Suporte</p>
                <a href="mailto:suporte@panther.com.br" className="text-sm text-blue-400 hover:underline">suporte@panther.com.br</a>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
              <Globe className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-white mb-0.5">Documentação Online</p>
                <a href="https://docs.panther.com.br" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:underline">docs.panther.com.br</a>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
              <Info className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-white mb-0.5">Versão do Sistema</p>
                <p className="text-sm text-gray-400">
                  Panther Access {process.env.REACT_APP_VERSION || '0.1.0'}
                  {process.env.NODE_ENV === 'development' && process.env.REACT_APP_BUILD_COMMIT && (
                    <span className="ml-2 text-xs opacity-60">({process.env.REACT_APP_BUILD_COMMIT})</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
};
